import type { ReactNode } from 'react';
import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { ErrorState } from '../../shared/components/ErrorState';
import { EmptyState } from '../../shared/components/EmptyState';
import { describeApiError } from '../../shared/api/errors';

/**
 * Casca compartilhada das 4 telas de relatório: header com filtros, botão
 * "Gerar", e área de preview com botões de export.
 *
 * Cada relatório só preenche slots:
 *  - `filters`: form de filtros (Selects + campos)
 *  - `canGenerate`: validação leve antes de habilitar Gerar
 *  - `preview`: tabela/cards renderizando o resultado
 *  - `onExportPdf`/`onExportXlsx`: builders chamando `downloadAsPdf`/`Xlsx`
 *
 * Sem `useQuery` aqui — quem orquestra é a página via `useMutation` (gera
 * sob demanda).
 */
export interface RelatorioPageShellProps {
  title: string;
  subtitle?: string;
  filters: ReactNode;
  canGenerate: boolean;
  onGenerate: () => void;
  generating: boolean;
  hasResult: boolean;
  error: unknown;
  preview: ReactNode;
  emptyMessage: string;
  onExportPdf: () => void;
  onExportXlsx: () => void;
  /** Ações extras à esquerda dos exports (ex.: extrato de lançamentos via KB). */
  extraActions?: ReactNode;
}

export function RelatorioPageShell(props: RelatorioPageShellProps): JSX.Element {
  const {
    title,
    subtitle,
    filters,
    canGenerate,
    onGenerate,
    generating,
    hasResult,
    error,
    preview,
    emptyMessage,
    onExportPdf,
    onExportXlsx,
    extraActions,
  } = props;

  return (
    <Box sx={{ pb: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
            <Box sx={{ flex: 1, width: '100%' }}>{filters}</Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayArrowIcon />}
              onClick={onGenerate}
              disabled={!canGenerate || generating}
            >
              {generating ? 'Gerando…' : 'Gerar'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {Boolean(error) && !generating && (
        <ErrorState description={describeApiError(error)} onRetry={onGenerate} />
      )}

      {hasResult && !error && !generating && (
        <>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>{preview}</CardContent>
          </Card>
          <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
            {extraActions}
            <Button
              variant="outlined"
              startIcon={<TableViewIcon />}
              onClick={onExportXlsx}
              disabled={generating}
            >
              Exportar XLSX
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={onExportPdf}
              disabled={generating}
            >
              Exportar PDF
            </Button>
          </Stack>
        </>
      )}

      {!hasResult && !error && !generating && <EmptyState description={emptyMessage} />}
    </Box>
  );
}
